import { useCallback, useMemo } from "react";

import AccountIcon from "public/icons/account-icon.svg";
import ConcantListIcon from "public/icons/contact-list.svg";
import GiftOrders from "public/icons/gift-order-icon.svg";
import GiftSvg from "public/icons/gift.svg";
import HomeIcon from "public/icons/home-icon.svg";
import LatestUpdatesIcon from "public/icons/latest-update-icon.svg";
import PaidContentIcon from "public/icons/paid-content.svg";
import PhoneIcon from "public/icons/phone-icon.svg";
import ResetIcon from "public/icons/reset-icon.svg";
import RulesIcon from "public/icons/rules-icon.svg";
import SearchIcon from "public/icons/search-icon.svg";
import SweetOrHotIcon from "public/icons/sweet-or-hot-icon.svg";

import { useOpenModal } from "@/components/modal/utils/openModal";
import { SvgIcon } from "@/components/ui/svg-icon/SvgIcon";
import { useAuthContext } from "@/context/auth-renderer/Auth.context";
import { getIsNewLeftbarDesign } from "@/lib/feature-flags";
import { projectIs } from "@/lib/feature-flags/featureFlagsDeside.helpers";

export const useNavLinksArray = (isSidebarVersion = false) => {
  const { openModal, removeOne } = useOpenModal();
  const openForgotPasswordModal = useCallback(
    () => openModal("FORGOT_PASSWORD_FORM_MODAL", { onClose: removeOne }),
    [openModal, removeOne],
  );

  const authContext = useAuthContext();
  const isNewLeftbarDesign = getIsNewLeftbarDesign();
  const openRegisterModal = useCallback(
    () => openModal("REGISTER_FORM_MODAL", { onClose: removeOne }),
    [openModal, removeOne],
  );

  const listItemsMap = useMemo(() => {
    const openInformationModal = (str: string) =>
      openModal("INFORMATION_MODAL", {
        message: str,
        statusReg: "success",
      });

    // For unAuth + new leftbar design
    const navigationItemsUnauthNewLeftbar = [
      {
        iconComponent: () => (
          <SvgIcon width={20} height={20}>
            <HomeIcon />
          </SvgIcon>
        ),
        label: "Home",
        path: "/home",
      },
      {
        iconComponent: () => (
          <SvgIcon width={20} height={20}>
            <SearchIcon />
          </SvgIcon>
        ),
        label: "Search",
        path: "/search",
      },
      {
        iconComponent: () => (
          <SvgIcon>
            <ResetIcon />
          </SvgIcon>
        ),
        label: "Request password",
        onClick: openForgotPasswordModal,
      },
      {
        iconComponent: PhoneIcon,
        label: "Contact Us",
        path: projectIs(["GM"]) ? "/t_contact.html" : "/page?CONTACT",
      },
    ];
    // --------------------------------------------

    // For unAuth + old leftbar design
    const navigationItemsUnauthOldLeftbar = [
      {
        iconComponent: () => {
          return (
            <SvgIcon width={20} height={20}>
              <SearchIcon />
            </SvgIcon>
          );
        },
        label: "Search",
        path: "/search",
      },
      {
        iconComponent: AccountIcon,
        label: "Register",
        onClick: openRegisterModal,
      },
      {
        iconComponent: () => {
          return (
            <SvgIcon>
              <ResetIcon />
            </SvgIcon>
          );
        },
        label: "Request password",
        onClick: openForgotPasswordModal,
      },
      {
        iconComponent: PhoneIcon,
        label: "Contact Us",
        path: projectIs(["GM"]) ? "/t_contact.html" : "/page?CONTACT",
      },
    ];
    // --------------------------------------------

    // For unAuth + old leftbar design + in drawer (mobile)
    const navigationItemsUnauthOldLeftbarInDrawer = [
      {
        iconComponent: () => (
          <SvgIcon width={20} height={26}>
            <HomeIcon />
          </SvgIcon>
        ),
        label: "Home",
        path: "/home",
      },
      ...navigationItemsUnauthOldLeftbar,
    ];
    // --------------------------------------------

    // For auth user
    const navigationItemsAuth = [
      {
        iconComponent: () => (
          <SvgIcon width={20} height={20}>
            <LatestUpdatesIcon />
          </SvgIcon>
        ),
        label: "Latest update",
        path: "/newsfeed",
      },
      {
        iconComponent: () => (
          <SvgIcon width={20} height={20}>
            <SearchIcon />
          </SvgIcon>
        ),
        label: "Search",
        path: "/search",
      },
      {
        iconComponent: () => (
          <SvgIcon width={20} height={20}>
            <SweetOrHotIcon />
          </SvgIcon>
        ),
        label: "Sweet or hot",
        onClick: () => openInformationModal("Sweet or Hot here"), //TODO add SweetModal here
      },
      {
        iconComponent: () => (
          <SvgIcon width={20} height={20}>
            <GiftSvg />
          </SvgIcon>
        ),
        label: "Send a gift",
        path: "/send-a-gift",
      },
      {
        iconComponent: () => (
          <SvgIcon sx={{ paddingLeft: "2px" }} width={24} height={24}>
            <PhoneIcon />
          </SvgIcon>
        ),
        label: "Phone calls",
        path: "/phone-calls",
      },
      {
        iconComponent: () => (
          <SvgIcon width={20} height={20}>
            <GiftOrders />
          </SvgIcon>
        ),
        label: "Gift orders",
        path: "/gift-orders",
      },
      {
        iconComponent: () => (
          <SvgIcon width={20} height={20}>
            <ConcantListIcon />
          </SvgIcon>
        ),
        label: "My contact list",
        path: "/contacts",
      },
      {
        iconComponent: () => (
          <SvgIcon width={20} height={20}>
            {/* need normal svg icon */}
            <PaidContentIcon />
          </SvgIcon>
        ),
        label: "Paid Content",
        path: "/paid-content",
      },
      {
        iconComponent: () => (
          <SvgIcon width={20} height={20}>
            <RulesIcon />
          </SvgIcon>
        ),
        label: "Rules",
        path: "/page?USAGE_RULES",
      },
    ];
    // --------------------------------------------

    if (!authContext.isAuthenticated && isNewLeftbarDesign) {
      return navigationItemsUnauthNewLeftbar;
    } else if (
      !authContext.isAuthenticated &&
      !isNewLeftbarDesign &&
      isSidebarVersion
    ) {
      return navigationItemsUnauthOldLeftbarInDrawer;
    } else if (!authContext.isAuthenticated && !isNewLeftbarDesign) {
      return navigationItemsUnauthOldLeftbar;
    } else {
      return navigationItemsAuth;
    }
  }, [
    openForgotPasswordModal,
    openRegisterModal,
    authContext.isAuthenticated,
    isNewLeftbarDesign,
    isSidebarVersion,
    openModal,
  ]);

  return listItemsMap;
};
